<template>
  <InvalidProperty v-if="!propsValidation.isValid" :message="propsValidation.message" />
  <template v-else>
    <div v-if="data" part="analysis-channel" class="analysis-channel" :class="{[props.dir]: true}">
      <div part="analysis-channel-diamond-block" class="analysis-channel-diamond-block">
        <div v-if="props.name" part="analysis-channel-title" class="analysis-channel-title">{{props.name}}</div>

        <template v-if="props.showDiamond">
          <div part="analysis-channel-diamond" class="analysis-channel-diamond">
            <Diamond part="nms-diamond" class="nms-diamond" :values="diamond"/>
          </div>
        </template>  
      </div>
      <div part="analysis-channel-info-block" class="analysis-channel-info-block">
        <template v-if="props.showDiamond">
          <RiskBlocks v-if="data.risk" :risk="data.risk" :localeTexts="Locale.analysisReport.risk" />

          <div class="bars-wrapper">
            <BarSeparator v-for="line of data.percentageLines" :line="line" />
            <div class="bars-legend">
              <div v-for="item in legend" class="legend-item">
                <div class="legend-color" :style="{backgroundColor: item.color}"></div>
                <div class="legend-text">{{item.text}}</div>
              </div>
            </div>
          </div>
        </template>

        <CallTags v-if="props.showTags" :tags="data.callTags" :localeTexts="Locale.analysisReport"></CallTags>
      </div>
    </div>
  </template>
</template>

<script>
const propsDefinition = {
  name: {
    required: false,
  },
  report: {
    required: true,
    typeof: "object",
    existProps: ['reports', 'segments']
  },
  channel:{
    required: false,
    typeof: "number",
    default: 0,
  },
  localeTexts: {
    required: false,
    typeof: "object",
    existProps: ['analysisReport', 'emotionDiamond'],
  },
  language: {
    required: false,
  },
  dir: {
    required: false,
  },
  showDiamond:{
    required: false,
    typeof: "boolean",
    default: true,
  },
  showTags:{
    required: false,
    typeof: "boolean",
    default: true,
  },
}
</script>
<script setup>
import Diamond from "@/components/EmotionDiamondLibrary/blocks/Diamond.ce.vue";
import RiskBlocks from "./components/RiskBlocks.vue";
import CallTags from "./components/CallTags.vue";
import BarSeparator from "./components/BarSeparator.vue";

import InvalidProperty from '@/components/InvalidProperty.vue';
import {getDefinePropsObject, getLocaleTexts, validateProps} from "@/helpers/helper.js";
import {computed} from "vue";
import {diamondHeadersDictionary} from "@/utils/dictionary.js";
import {getRiskType, headerPos} from "@/utils/emlo.js";

const stringifyProps = defineProps(getDefinePropsObject(propsDefinition));

const {propsValidation, props} = validateProps(propsDefinition, stringifyProps);
const Locale = getLocaleTexts(props, (locale) => ({analysisReport: locale.analysisReport, emotionDiamond: locale.emotionDiamond}));

const data = computed(() => {
  if(!props.value.report) return null;

  return channelAnalysis(props.value.report, props.value.channel);
})

const diamond = computed(() => JSON.stringify(data.value?.diamond));

const colors = {
  normal: 'var(--diamond-bar-normal-color, #FFFFFF)',
  low: 'var(--diamond-bar-low-color, #FFD3A6)',
  medium: 'var(--diamond-bar-medium-color, #FFAC59)',
  high: 'var(--diamond-bar-high-color, #FF8000)',
}

const legend = [
  // {color: colors.normal, text: Locale.value.analysisReport.legend.normal},
  {color: colors.low, text: Locale.value.analysisReport.legend.low},
  {color: colors.medium, text: Locale.value.analysisReport.legend.medium},
  {color: colors.high, text: Locale.value.analysisReport.legend.high},
]

function channelAnalysis(content, channel){
  const report = {
    diamond: [],
    percentageLines: [],
    callTags: [],
  };
  if(!content.segments || !content.reports || !content.segments.data) return report;

  const validSegments = content.segments.data.filter(s => s[headerPos(content, 'validSegment')] && s[headerPos(content, 'channel')] === channel);
  const channelReport = content.reports["channel-"+channel];

  if(!channelReport) return report;

  if(channelReport.edp){
    for (const dk in diamondHeadersDictionary) {
      const value = channelReport.edp[dk]
      report.diamond.push({
        text: Locale.value.emotionDiamond[diamondHeadersDictionary[dk]],
        value: value,
      })
    }
  }

  const LVAPos = headerPos(content, 'offlineLVA-value') || headerPos(content, 'onlineLVA');
  if(LVAPos) {
    report.risk = validSegments.reduce((res, curr) => {
      const riskType = getRiskType(curr[LVAPos]);
      if(riskType) res[riskType]++;
      return res;
    }, {high: 0, medium: 0, suspected: 0})
  }

  report.percentageLines = [];

  const barsLogic1 = (val) => {
    return [
      // {value: Math.round(val?.noReactionPercentage || 0), color: colors.normal,},
      {value: Math.round(val?.lowPercentage || 0), color: colors.low,},
      {value: Math.round(val?.midPercentage || 0), color: colors.medium,},
      {value: Math.round(val?.highPercentage || 0), color: colors.high,},
    ].filter(b => b.value > 0);
  }

  const barsLogic2 = (val) => {
    return [
      {value: Math.round(val?.lowPercentage || 0), color: colors.low,},
      {value: Math.round((val?.midPercentage || 0) + (val?.normalReactionPercentage || 0)), color: colors.medium,},
      {value: Math.round(val?.highPercentage || 0), color: colors.high,},
    ].filter(b => b.value > 0);
  }
  const barsLogic3 = (val) => {
    return [
      // {value: Math.round(100 - Math.round(val?.highPercentage || 0)), color: colors.normal,},
      {value: Math.round(val?.highPercentage || 0), color: colors.high,},
    ].filter(b => b.value > 0);
  }

  const lines = [];
  if (channelReport.profile?.energy) {
    lines.push({
      title: Locale.value.analysisReport.energy,
      bars: barsLogic1(channelReport.profile.energy),
    })
  }
  if (channelReport.profile?.stress) {
    lines.push({
      title: Locale.value.analysisReport.stress,
      bars: barsLogic1(channelReport.profile.stress),
    })
  }
  if (channelReport.profile?.mentalEffort) {
    lines.push({
      title: Locale.value.analysisReport.mentalEffort,
      bars: barsLogic1(channelReport.profile.mentalEffort),
    })
  }
  if (channelReport.profile?.excitement) {
    lines.push({
      title: Locale.value.analysisReport.excitement,
      bars: barsLogic2(channelReport.profile.excitement),
    })
  }
  if (channelReport.profile?.uncertainty) {
    lines.push({
      title: Locale.value.analysisReport.confidence,
      bars: barsLogic2(channelReport.profile.uncertainty),
    })
  }
  if (channelReport.profile?.overallCognitiveActivity) {
    lines.push({
      title: Locale.value.analysisReport.distress,
      bars: barsLogic3(channelReport.profile.overallCognitiveActivity),
    })
  }

  for (const line of lines) {
    if (line.bars.reduce((sum, b) => sum + b.value, 0) > 100) {
      const max = Math.max(...line.bars.map(b => b.value));
      const otherSum = line.bars.reduce((sum, b) => sum + b.value, 0) - max;
      const maxBar = line.bars.findIndex(b => b.value === max);
      line.bars[maxBar].value = 100 - otherSum;
    }
  }

  report.percentageLines = lines;

  report.callTags = channelReport.tags || [];

  return report;
}

</script>

<style lang="scss" src="./AnalysisChannelReport.scss" />
